// @flow

import * as React from 'react';
import {defineMessage} from 'react-intl';
import session from '../session';
import {
  BillingPricePicker,
  DefaultCards,
  RunServerCommandShell,
} from '../../../components/left-col';
import {automaticTaxStep} from '../../checkout/html/steps';

import type {StepsConfig} from '../../../data/GuideTypes';

function code(str) {
  return <code>{str}</code>;
}

export default ([
  {
    id: 'pricing-model',
    section: 'create-pricing-model',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.billing.react.business.heading',
        defaultMessage: 'Create pricing model',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.billing.react.business.body',
        defaultMessage:
          'The pricing model comprises the products or services you sell, how much they cost, what currency to use for payments, and the interval to charge for the subscription. To build the pricing model, you use <a>Products</a>—what you sell—and <b>Prices</b>—how much and how often to charge for your products. You need to create at least one product before you can charge for it. (To create the pricing model within this builder, you must be signed in to your Stripe account. To create the pricing model without signing in, see <c>create the pricing model</c>.)',
      }),
      values: {
        a: (str) => (
          <a href="/docs/api/products" target="_blank">
            {str}
          </a>
        ),
        b: (str) => (
          <a href="/docs/api/prices" target="_blank">
            {str}
          </a>
        ),
        c: (str) => (
          <a
            href="/docs/billing/subscriptions/checkout#create-pricing-model"
            target="_blank"
          >
            {str}
          </a>
        ),
      },
    },
    Component: BillingPricePicker,
  },
  {
    id: 'preview-page',
    section: 'build-page',
    heading: {
      message: defineMessage({
        id: 'docsShell.ib.billing.react.preview-page.heading',
        defaultMessage: 'Add a pricing preview page',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.ib.billing.react.preview-page.body',
        defaultMessage:
          'Add a page to your site to display your product and offer your customers an option to subscribe to it. When they click the checkout button, they’re redirected to a Stripe-hosted <a>Checkout</a> page. The order is finalized when your customer is sent to the Checkout page—they can’t modify it after that point.',
      }),
      values: {
        a: (str) => (
          <a href="/docs/payments/checkout" target="_blank">
            {str}
          </a>
        ),
      },
    },
  },
  {
    id: 'add-checkout-button',
    section: 'build-page',
    heading: {
      message: defineMessage({
        id: 'docsShell.ib.billing.react.add-checkout-button.heading',
        defaultMessage: 'Add a checkout button',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.ib.billing.react.add-checkout-button.body',
        defaultMessage:
          'Add a button to your order preview page. Clicking this button redirects your customer to the Stripe-hosted Checkout page. The <code>lookup_key</code> was added when you created the product and price in the first step. When the form is submitted it is used to retrieve the <code>price_id</code> on the server.',
      }),
      values: {
        code,
      },
    },
  },
  {
    id: 'success-page',
    section: 'build-page',
    heading: {
      message: defineMessage({
        id: 'docsShell.ib.billing.react.success-page.heading',
        defaultMessage: 'Add a success page',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.ib.billing.react.success-page.body',
        defaultMessage:
          'Create a success page for the URL you provided as the Checkout Session <code>success_url</code> to display order confirmation messaging or order details to your customer. Stripe redirects to this page after the customer successfully completes the checkout.',
      }),
      values: {
        code,
      },
    },
  },
  {
    id: 'add-portal-button',
    section: 'build-page',
    heading: {
      message: defineMessage({
        id: 'docsShell.ib.billing.react.add-portal-button.heading',
        defaultMessage: 'Add a customer portal button',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.ib.billing.react.add-portal-button.body',
        defaultMessage:
          'Add a button to redirect to the customer portal to allow customers to manage their subscription. When your customer clicks this button, they’re redirected to the Stripe-hosted customer portal page.',
      }),
    },
  },
  {
    id: 'fetch-customer-portal',
    section: 'build-page',
    heading: {
      message: defineMessage({
        id: 'docsShell.ib.billing.react.fetch-customer-portal.heading',
        defaultMessage: 'Redirect to the customer portal session',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.ib.billing.react.fetch-customer-portal.body',
        defaultMessage:
          'Make a request to the endpoint on your server to redirect to a new customer portal session. This request uses the session <code>id</code> from the <a>Checkout session</a> that was created. The <code>session_id</code> is used for demonstration purposes to retrieve the <code>customer_id</code>. In a production environment, this is typically stored alongside the authenticated user in your database.',
      }),
      values: {
        code,
        a: (str) => (
          <a
            href="/docs/api/checkout/sessions/object#checkout_session_object-id"
            target="_blank"
          >
            {str}
          </a>
        ),
      },
    },
  },
  {
    file: 'server',
    id: 'setup',
    section: 'setup-stripe',
  },
  {
    file: 'server',
    id: 'create',
    section: 'setup-stripe',
    heading: {
      message: defineMessage({
        id: 'docsShell.ib.billing.react.create.heading',
        defaultMessage: 'Create a Checkout Session',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.ib.billing.react.create.body',
        defaultMessage: `Add an endpoint on your server that creates a <a>Checkout Session</a>. A Checkout Session controls what your customer sees in the Stripe-hosted payment page such as line items, the order amount and currency, and acceptable payment methods.`,
      }),
      values: {
        a: (str) => (
          <a href="/docs/api/checkout/sessions" target="_blank">
            {str}
          </a>
        ),
      },
    },
    response: session.react,
  },
  {
    file: 'server',
    id: 'price',
    section: 'setup-stripe',
    heading: {
      message: defineMessage({
        id: 'docsShell.ib.billing.react.price.heading',
        defaultMessage: 'Get the price from lookup key',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.ib.billing.react.price.body',
        defaultMessage: `Use <a>Prices</a> to retrieve the price of the product you are selling by passing in the lookup key you created above.`,
      }),
      values: {
        a: (str) => (
          <a href="/docs/api/prices/list" target="_blank">
            {str}
          </a>
        ),
      },
    },
  },
  {
    file: 'server',
    id: 'payment-methods',
    section: 'setup-stripe',
    heading: {
      message: defineMessage({
        id: 'docsShell.ib.billing.react.payment-methods.heading',
        defaultMessage: 'Specify payment methods',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.ib.billing.react.payment-methods.body',
        defaultMessage: `Checkout supports <severalA>several payment methods</severalA> beyond cards. If you pass multiple payment methods, Checkout dynamically reorders them to prioritize the most relevant payment methods based on the customer’s location and other characteristics. If you accept cards as a payment method, Apple Pay and Google Pay are displayed in Stripe Checkout when applicable.`,
      }),
      values: {
        severalA: (str) => (
          <a href="/docs/payments/checkout/payment-methods" target="_blank">
            {str}
          </a>
        ),
      },
    },
  },
  {
    file: 'server',
    id: 'line-items',
    section: 'setup-stripe',
    heading: {
      message: defineMessage({
        id: 'docsShell.ib.billing.react.line-items.heading',
        defaultMessage: 'Define the line items',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.ib.billing.react.line-items.body',
        defaultMessage: `Always keep sensitive information about your product inventory, like price and availability, on your server to prevent customer manipulation from the client. Pass in the pre-defined price ID retrieved above.`,
      }),
    },
  },
  {
    file: 'server',
    id: 'mode',
    section: 'setup-stripe',
    heading: {
      message: defineMessage({
        id: 'docsShell.ib.billing.react.mode.heading',
        defaultMessage: 'Choose the mode',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.ib.billing.react.mode.body',
        defaultMessage: `Use <code>subscription</code> mode to set up a subscription. Checkout also has <code>payment</code> and  <code>setup</code> modes. Learn more about <paymentA>payment</paymentA> and <setupA>setup</setupA> modes in the docs.`,
      }),
      values: {
        paymentA: (str) => (
          <a href="/docs/checkout/quickstart" target="_blank">
            {str}
          </a>
        ),
        setupA: (str) => (
          <a href="/docs/payments/save-and-reuse#checkout" target="_blank">
            {str}
          </a>
        ),
        code,
      },
    },
  },
  {
    file: 'server',
    id: 'urls',
    section: 'setup-stripe',
    heading: {
      message: defineMessage({
        id: 'docsShell.ib.billing.react.urls.heading',
        defaultMessage: 'Supply success and cancel URLs',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.ib.billing.react.urls.body',
        defaultMessage: `Specify URLs for success and cancel pages—make sure they are publicly accessible so Stripe can redirect customers to them. You can also handle both the success and canceled states with the same URL. Add session_id query parameter at the end of your URL to be able to retrieve the customer later.`,
      }),
    },
  },
  {
    file: 'server',
    id: 'redirect',
    section: 'setup-stripe',
    heading: {
      message: defineMessage({
        id: 'docsShell.ib.billing.react.redirect.heading',
        defaultMessage: 'Redirect to Checkout',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.ib.billing.react.redirect.body',
        defaultMessage: `After creating the session, redirect your customer to the URL returned in the response (either the success or cancel URL).`,
      }),
    },
  },
  {
    id: 'portal',
    section: 'setup-stripe',
    heading: {
      message: defineMessage({
        id: 'docsShell.ib.billing.react.portal.heading',
        defaultMessage: 'Create a customer portal session',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.ib.billing.react.portal.body',
        defaultMessage:
          "Add an endpoint on your server that creates a customer portal session. The customer portal is a secure, Stripe-hosted page that lets your customers manage their subscriptions and billing details. A session creates an instance of the portal for a specific customer. When the customer visits the session's URL, they can manage their subscriptions and billing details. ",
      }),
    },
  },
  {
    id: 'redirect-portal',
    section: 'setup-stripe',
    heading: {
      message: defineMessage({
        id: 'docsShell.ib.billing.react.redirect.portal.heading',
        defaultMessage: 'Redirect to customer portal',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.ib.billing.react.redirect.portal.body',
        defaultMessage: `After creating the portal session, redirect your customer to the URL returned in the response (either the success or cancel URL).`,
      }),
    },
  },
  {
    id: 'provision-access-webhooks',
    section: 'setup-stripe',
    heading: {
      message: defineMessage({
        id: 'docsShell.ib.billing.react.webhooks.heading',
        defaultMessage: 'Provision access',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.ib.billing.react.webhooks.body',
        defaultMessage:
          'After a successful payment, a confirmation page is displayed. At this point it’s safe to provision the product for the customer. This means verifying the status of the subscription is <code>active</code> and granting the customer access to the products and features they subscribed to. Create your <code>/webhook</code> endpoint and get your webhook secret key in the <a2>Dashboard</a2>.',
      }),
      values: {
        code,
        a2: (str) => (
          <a href="https://dashboard.stripe.com/test/webhooks">{str}</a>
        ),
      },
    },
  },
  {
    id: 'run-server',
    section: 'testing',
    heading: {
      message: defineMessage({
        id: 'docsShell.ib.billing.react.run-server.heading',
        defaultMessage: 'Run the application',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.ib.billing.react.run-server.body',
        defaultMessage:
          'Start your server. It automatically opens a browser window to http://localhost:3000/checkout',
      }),
    },
    Component: RunServerCommandShell,
  },
  {
    id: 'testing',
    section: 'testing',
    heading: {
      message: defineMessage({
        id: 'docsShell.ib.billing.react.testing.heading',
        defaultMessage: 'Try it out',
      }),
    },
    testing: true,
    body: {
      message: defineMessage({
        id: 'docsShell.ib.billing.react.testing.body',
        defaultMessage:
          'Click the checkout button to be redirected to the Stripe Checkout page. Use any of these test cards to simulate a payment.',
      }),
    },
    Component: DefaultCards,
  },
  {
    extension: 'extra',
    id: 'add-trial',
    heading: {
      message: defineMessage({
        id: 'docsShell.ib.billing.react.add-trial.heading',
        defaultMessage: 'Add trials',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.ib.billing.react.add-trial.body',
        defaultMessage: 'Attach a trial period to a Checkout session.',
      }),
    },
    steps: [
      {
        id: 'trial-period-days',
        heading: {
          message: defineMessage({
            id: 'docsShell.ib.billing.react.trial-period-days.heading',
            defaultMessage: 'Add a trial period',
          }),
        },
        body: {
          message: defineMessage({
            id: 'docsShell.ib.billing.react.trial-period-days.body',
            defaultMessage:
              'Use <code>subscription_data</code> to add an integer representing the number of <code>trial_period_days</code> before the customer is charged for the first time. Has to be at least 1.',
          }),
          values: {
            a: (str) => (
              <a
                href="/docs/api/checkout/sessions/create#create_checkout_session-subscription_data"
                target="_blank"
              >
                {str}
              </a>
            ),
            code,
          },
        },
      },
    ],
  },
  automaticTaxStep,
]: StepsConfig);
